/**
 * Deleting and archiving a folder. Deleting one with things in it says what goes with it ("3 folders and 41 items"),
 * ten or more asking for their number to be typed, as the trash's purges do; it all goes to the trash together (past
 * a thousand, in the background). Archiving asks whether what's inside is archived too.
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { FolderCounts, FolderDeleted, FolderOut, Job, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { whatInside } from "../../lib/folders.ts";
import { followJob } from "../../lib/jobs.ts";
import { spaceApi } from "../../lib/paths.ts";
import { asApiError, bump, useAction } from "../../lib/resource.ts";
import { toast } from "../../lib/toast.ts";
import { Button } from "../../ui/Button.tsx";
import { ConfirmDialog, Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Checkbox } from "../../ui/form.tsx";
import { TrashIcon } from "../../ui/icons.tsx";
import { typeCount } from "../trash/typeCount.ts";

const NOT_EMPTY = "folder_not_empty";

/** How many things a folder holds. */
const total = (c: FolderCounts) => c.folders + c.items;

/** Says what a delete did: what went, and what stayed (things in it the deleter doesn't see). */
function sayDeleted(d: FolderDeleted, name: string) {
  const what = whatInside(d);
  toast(what ? t("folders.deletedWith", { name, what }) : t("folders.deleted", { name }));
  if (d.left_in_place) {
    toast(t("folders.leftInPlace"));
  }
}

/** "Delete folder": asked first (what it holds, from the server when it knows better); then `onDone`. */
export function DeleteFolderDialog(p: {
  space: SpaceOut;
  open: boolean;
  folder: Pick<FolderOut, "id" | "name" | "counts">;
  onClose: () => void;
  onDone: () => void;
}) {
  const { folder } = p;
  // What it holds, as the server last said (a 409 when the page's count was stale).
  const [counts, setCounts] = useState<FolderCounts>(folder.counts);
  // Asked again, with what it holds now.
  const [again, setAgain] = useState(false);
  const full = total(counts) > 0;
  const [remove] = useAction(async (recursive: boolean) => {
    try {
      const r = await api.del<FolderDeleted | Job>(spaceApi(p.space, `/folders/${folder.id}`), {
        query: { recursive },
      });
      if ("action" in r) {
        toast(t("folders.deleting", { name: folder.name }));
        await followJob(p.space, r);
      } else {
        sayDeleted(r, folder.name);
      }
      bump();
      p.onDone();
    } catch (e) {
      const err = asApiError(e);
      if (err.code === NOT_EMPTY) {
        setCounts((err.info["counts"] as FolderCounts | undefined) ?? counts);
        setAgain(true);
        return;
      }
      throw err;
    }
  });
  return (
    <ConfirmDialog
      open={p.open || again}
      onClose={() => {
        setAgain(false);
        p.onClose();
      }}
      title={t("folders.deleteTitle", { name: folder.name })}
      message={
        full ? (
          <>
            <p>{t("folders.deleteFull", { what: whatInside(counts) })}</p>
            {counts.hidden ? <p class="mt-2">{t("folders.deleteHidden")}</p> : null}
          </>
        ) : (
          t("folders.deleteEmpty")
        )
      }
      confirmLabel={full ? t("folders.deleteAll") : t("common.delete")}
      confirmIcon={<TrashIcon size={14} />}
      {...(full ? typeCount(total(counts)) : {})}
      onConfirm={() => remove(full)}
    />
  );
}

/** Archiving a folder (or bringing it back), asking about what's in it when it holds anything. */
export function ArchiveFolderDialog(p: {
  space: SpaceOut;
  open: boolean;
  folder: FolderOut;
  onClose: () => void;
  onDone: (f: FolderOut | null) => void;
}) {
  const { folder } = p;
  const archiving = !folder.archived;
  const [inside, setInside] = useState(true);
  const full = total(folder.counts) > 0;
  const [run, busy] = useAction(async () => {
    const body = { archived: archiving, ...(full ? { apply_to_contents: inside } : {}), ver: folder.ver };
    const r = await api.patch<FolderOut | Job>(spaceApi(p.space, `/folders/${folder.id}`), body);
    if ("action" in r) {
      await followJob(p.space, r);
    }
    toast(t(archiving ? "folders.archived" : "folders.unarchived", { name: folder.name }));
    bump();
    p.onClose();
    p.onDone("action" in r ? null : r);
  });
  const title = t(archiving ? "folders.archiveTitle" : "folders.unarchiveTitle", { name: folder.name });
  return (
    <Dialog open={p.open} onClose={p.onClose} title={title}>
      <p class="text-sm text-zinc-700">{t(archiving ? "folders.archiveWhat" : "folders.unarchiveWhat")}</p>
      {full ? (
        <Checkbox
          label={t(archiving ? "folders.archiveInside" : "folders.unarchiveInside", {
            what: whatInside(folder.counts),
          })}
          checked={inside}
          onCheck={setInside}
        />
      ) : null}
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" busy={busy} onClick={() => run()}>
          {t(archiving ? "item.archive" : "item.unarchive")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
